import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

/** The finance module's wire types and hooks: ledger, statements, expenses, petty cash, assets, opening balances. */

export type AccountType = 'Asset' | 'Liability' | 'Equity' | 'Income' | 'Expense'
export type JournalSource =
  | 'Manual' | 'SalesInvoice' | 'CreditNote' | 'Receipt' | 'GoodsReceivedNote' | 'PurchaseInvoice' | 'SupplierPayment'
  | 'StockTransfer' | 'StockAdjustment' | 'ExpenseVoucher' | 'Contra' | 'LandedCostAdjustment' | 'OpeningBalance'
  | 'YearEndClose' | 'PettyCash' | 'FixedAsset' | 'Mpesa' | 'CashUp' | 'BankReconciliation'
export type ExpenseStatus = 'Pending' | 'Posted' | 'Rejected'
export type ExpensePaidFrom = 'Account' | 'PettyCash'
export type AssetCategory = 'Buildings' | 'FurnitureAndFittings' | 'MotorVehicles' | 'ComputersAndPos'
export type AssetStatus = 'Active' | 'Disposed'

export type Account = {
  id: string; code: string; name: string; type: AccountType; isControlAccount: boolean; isBankAccount: boolean; isActive: boolean; balance: number
  /** Where it sits in the chart: cash and bank, control, inventory, fixed assets, tax… */
  category: string
  /** Reduces its own type's total: accumulated depreciation, sales returns. */
  isContra: boolean
}

export type BalancePoint = { on: string; assets: number; liabilities: number; equity: number }
export type AccountSummary = {
  assets: number; liabilities: number; equity: number
  /** Percent against the last month-end; absent where that was nothing. */
  assetsChange?: number; liabilitiesChange?: number; equityChange?: number
  accounts: number; assetAccounts: number; liabilityAccounts: number; otherAccounts: number
  trend: BalancePoint[]
}

export type LedgerLine = {
  journalEntryId: string; journalCode: string; entryDate: string; source: JournalSource; narration: string; lineNarration?: string
  sourceDocumentType?: string; sourceDocumentId?: string; sourceDocumentCode?: string; locationId?: string
  debit: number; credit: number; runningBalance: number
}

export type AccountLedger = {
  accountCode: string; accountName: string; type: AccountType; from: string; to: string
  openingBalance: number; lines: LedgerLine[]; closingBalance: number; truncated: boolean
}

export type JournalListItem = {
  id: string; code: string; entryDate: string; source: JournalSource; narration: string
  sourceDocumentType?: string; sourceDocumentId?: string; sourceDocumentCode?: string; amount: number; reversed: boolean; postedBy?: string
}

export type JournalDetail = {
  id: string; code: string; entryDate: string; source: JournalSource; narration: string
  sourceDocumentType?: string; sourceDocumentId?: string; sourceDocumentCode?: string
  postedAt: string; postedBy?: string; reversedByJournalEntryId?: string; reversedByCode?: string; canReverse: boolean
  lines: { accountCode: string; accountName: string; locationName?: string; debit: number; credit: number; narration?: string }[]
  total: number
}

export type TrialBalance = {
  asOf: string; locationId?: string
  lines: { code: string; name: string; type: AccountType; debit: number; credit: number; balance: number }[]
  totalDebit: number; totalCredit: number; isBalanced: boolean
}

export type StatementSection = { title: string; lines: { accountCode: string; accountName: string; amount: number }[]; total: number }
export type ProfitAndLoss = { from: string; to: string; locationId?: string; income: StatementSection; costOfSales: StatementSection; grossProfit: number; expenses: StatementSection; netProfit: number }
export type BalanceSheet = { asOf: string; assets: StatementSection; liabilities: StatementSection; equity: StatementSection; unclosedProfit: number; totalEquity: number; difference: number; balances: boolean }
export type CashFlow = { from: string; to: string; openingCash: number; sections: { title: string; lines: { label: string; amount: number }[]; total: number }[]; netChange: number; closingCash: number }
export type CashPositionLine = { accountCode: string; accountName: string; locationId?: string; locationName?: string; balance: number }

export type Period = { year: number; month: number; label: string; isLocked: boolean; lockedAt?: string; lockedBy?: string; reopenReason?: string }
export type YearEnd = { year: number; closedAt: string; closedBy?: string; netProfit: number; journalEntryId: string }

export type ExpenseCategory = { id: string; code: string; name: string; parentId?: string; accountCode?: string; isActive: boolean }

export type ExpenseListItem = {
  id: string; code: string; status: ExpenseStatus; paidFrom: ExpensePaidFrom; locationName: string; expenseDate: string
  categoryName: string; payee: string; description: string; total: number; requestedBy?: string
}

export type ExpenseDetail = {
  id: string; code: string; status: ExpenseStatus; locationId: string; locationName: string; expenseDate: string
  categoryId: string; categoryName: string; accountCode: string; payee: string; description: string; reference?: string
  netAmount: number; vatAmount: number; taxInvoiceNumber?: string; total: number; paidFrom: ExpensePaidFrom; paidFromAccountCode?: string
  requestedBy?: string; decidedBy?: string; decidedAt?: string; rejectionReason?: string; journalEntryId?: string; canApprove: boolean
  attachments: { id: string; url: string; uploadedAt: string }[]
}

export type PettyCashRow = {
  locationId: string; locationName: string; hasFund: boolean; floatAmount: number; balance: number
  pendingRequests: number; topUpToFloat: number; custodianUserId?: string; custodianName?: string
}

export type AssetListItem = {
  id: string; code: string; name: string; category: AssetCategory; status: AssetStatus; locationName: string
  purchaseDate: string; cost: number; accumulatedDepreciation: number; netBookValue: number; monthlyDepreciation: number
}

export type AssetDetail = AssetListItem & {
  locationId: string; residualValue: number; usefulLifeMonths: number; accountCode: string
  serialNumber?: string; supplier?: string; notes?: string; disposedOn?: string; disposalProceeds?: number
  depreciation: { year: number; month: number; amount: number }[]
  maintenance: { id: string; date: string; description: string; cost: number; paidFromAccountCode: string }[]
}

export type OpeningStatus = { posted: boolean; asOf?: string; postedAt?: string; postedBy?: string; capital?: number; journalEntryId?: string }

export type OpeningBalances = {
  asOf: string
  accounts: { accountCode: string; debit: number; credit: number; locationCode?: string }[]
  stock: { productCode: string; locationCode: string; quantity: number; unitCost: number }[]
  customers: { partyCode: string; amount: number; dueDate?: string; reference?: string }[]
  suppliers: { partyCode: string; amount: number; dueDate?: string; reference?: string }[]
  assets: { name: string; category: AssetCategory; locationCode: string; purchaseDate: string; cost: number; accumulatedDepreciation: number; residualValue: number; usefulLifeMonths: number }[]
  recogniseExistingStock: boolean
}

export type OpeningPreview = {
  problems: string[]; accountsDebit: number; accountsCredit: number; stockValue: number; unrecognisedStockValue: number
  debtors: number; creditors: number; assetsNetBookValue: number; capital: number; alreadyPosted: boolean
}

export const sourceLabels: Record<JournalSource, string> = {
  Manual: 'Manual journal', SalesInvoice: 'Sale', CreditNote: 'Credit note', Receipt: 'Receipt', GoodsReceivedNote: 'Goods received',
  PurchaseInvoice: 'Supplier bill', SupplierPayment: 'Supplier payment', StockTransfer: 'Transfer', StockAdjustment: 'Stock adjustment',
  ExpenseVoucher: 'Expense', Contra: 'Reversal', LandedCostAdjustment: 'Landed cost', OpeningBalance: 'Opening balances',
  YearEndClose: 'Year-end close', PettyCash: 'Petty cash', FixedAsset: 'Fixed asset',
  Mpesa: 'M-Pesa', CashUp: 'Till count', BankReconciliation: 'From the bank statement',
}

export const assetCategoryLabels: Record<AssetCategory, string> = {
  Buildings: 'Buildings', FurnitureAndFittings: 'Shelving & fittings', MotorVehicles: 'Vehicles', ComputersAndPos: 'Computers & POS',
}

export const accountTypeOrder: AccountType[] = ['Asset', 'Liability', 'Equity', 'Income', 'Expense']

/** Where a journal's document lives in the app, when it has a page. */
export function documentLink(type?: string, id?: string): string | null {
  if (!type || !id) return null
  switch (type) {
    case 'GoodsReceivedNote': return `/purchases/receiving/${id}`
    case 'PurchaseInvoice': return `/purchases/bills/${id}`
    case 'StockTransfer': return `/warehouse/orders/${id}`
    case 'StockAdjustment': return `/warehouse/adjustments/${id}`
    case 'ExpenseVoucher': return `/finance/expenses/${id}`
    case 'FixedAsset': return `/finance/assets/${id}`
    case 'JournalEntry': return `/finance/journals/${id}`
    case 'Receipt': return `/finance/receipts/${id}`
    case 'MpesaPayment': return `/finance/mpesa?payment=${id}`
    default: return null
  }
}

export const startOfMonth = (iso: string) => `${iso.slice(0, 8)}01`
export const startOfYear = (iso: string) => `${iso.slice(0, 5)}01-01`

// --- hooks ---------------------------------------------------------------------------------

const qs = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

export const financeKeys = { all: ['finance'] as const }

export const useAccounts = () => useQuery({ queryKey: ['finance', 'accounts'], queryFn: ({ signal }) => api<Account[]>('/api/finance/accounts', { signal }) })
export const useAccountSummary = () =>
  useQuery({ queryKey: ['finance', 'accounts', 'summary'], queryFn: ({ signal }) => api<AccountSummary>('/api/finance/accounts/summary', { signal }) })

export const useAccountLedger = (code: string, from: string, to: string, locationId: string) =>
  useQuery({
    queryKey: ['finance', 'ledger', code, from, to, locationId],
    queryFn: ({ signal }) => api<AccountLedger>(`/api/finance/accounts/${code}/ledger${qs({ from, to, locationId })}`, { signal }),
  })

export const useJournals = (from: string, to: string, source: string, search: string) =>
  useQuery({
    queryKey: ['finance', 'journals', from, to, source, search],
    queryFn: ({ signal }) => api<JournalListItem[]>(`/api/finance/journals${qs({ from, to, source, search })}`, { signal }),
  })

export const useJournal = (id: string) => useQuery({ queryKey: ['finance', 'journal', id], queryFn: ({ signal }) => api<JournalDetail>(`/api/finance/journals/${id}`, { signal }) })

/** One band of the VAT return: what it was worth, and the tax on it. */
export type VatBand = { net: number; vat: number; documents: number }

/** The return against the ledger — two routes to the same figure, printed side by side (D-264). */
export type VatLedgerCheck = {
  outputAccount: string; outputMovement: number; outputDifference: number
  inputAccount: string; inputMovement: number; inputDifference: number
  agrees: boolean
}

export type VatEtims = { transmitted: number; pending: number; failed: number; notSubmitted: number; outstanding: number }

export type VatReturn = {
  from: string; to: string
  standardSales: VatBand; zeroRatedSales: VatBand; exemptSales: VatBand
  standardCredits: VatBand; zeroRatedCredits: VatBand; exemptCredits: VatBand
  outputVat: number
  standardPurchases: VatBand
  inputVat: number
  netPayable: number
  ledger: VatLedgerCheck
  etims: VatEtims
  notes: string[]
}

/** A period's VAT, in the shape the KRA return asks for. Defaults to last month. */
export const useVatReturn = (from: string, to: string) =>
  useQuery({ queryKey: ['finance', 'vat', from, to], queryFn: ({ signal }) => api<VatReturn>(`/api/finance/vat${qs({ from, to })}`, { signal }) })

export const useTrialBalance = (asOf: string, locationId: string) =>
  useQuery({ queryKey: ['finance', 'tb', asOf, locationId], queryFn: ({ signal }) => api<TrialBalance>(`/api/finance/trial-balance${qs({ asOf, locationId })}`, { signal }) })

export const useProfitAndLoss = (from: string, to: string, locationId: string) =>
  useQuery({ queryKey: ['finance', 'pnl', from, to, locationId], queryFn: ({ signal }) => api<ProfitAndLoss>(`/api/finance/statements/profit-and-loss${qs({ from, to, locationId })}`, { signal }) })

export const useBalanceSheet = (asOf: string, enabled: boolean) =>
  useQuery({ queryKey: ['finance', 'bs', asOf], queryFn: ({ signal }) => api<BalanceSheet>(`/api/finance/statements/balance-sheet${qs({ asOf })}`, { signal }), enabled })

export const useCashFlow = (from: string, to: string, enabled: boolean) =>
  useQuery({ queryKey: ['finance', 'cf', from, to], queryFn: ({ signal }) => api<CashFlow>(`/api/finance/statements/cash-flow${qs({ from, to })}`, { signal }), enabled })

export const useCashPosition = (asOf: string, enabled: boolean) =>
  useQuery({ queryKey: ['finance', 'cash', asOf], queryFn: ({ signal }) => api<CashPositionLine[]>(`/api/finance/statements/cash-position${qs({ asOf })}`, { signal }), enabled })

export const usePeriods = () => useQuery({ queryKey: ['finance', 'periods'], queryFn: ({ signal }) => api<Period[]>('/api/finance/periods', { signal }) })
export const useYearEnds = () => useQuery({ queryKey: ['finance', 'year-ends'], queryFn: ({ signal }) => api<YearEnd[]>('/api/finance/year-ends', { signal }) })

export const useExpenseCategories = () =>
  useQuery({ queryKey: ['finance', 'categories'], queryFn: ({ signal }) => api<ExpenseCategory[]>('/api/expense-categories', { signal }), staleTime: 5 * 60_000 })

export const useExpenses = (status: string, paidFrom: string) =>
  useQuery({ queryKey: ['finance', 'expenses', status, paidFrom], queryFn: ({ signal }) => api<ExpenseListItem[]>(`/api/expenses${qs({ status, paidFrom })}`, { signal }) })

export const useExpense = (id: string) => useQuery({ queryKey: ['finance', 'expense', id], queryFn: ({ signal }) => api<ExpenseDetail>(`/api/expenses/${id}`, { signal }) })

export const usePettyCash = () => useQuery({ queryKey: ['finance', 'petty-cash'], queryFn: ({ signal }) => api<PettyCashRow[]>('/api/petty-cash', { signal }) })

export const usePettyCashStatement = (locationId: string) =>
  useQuery({ queryKey: ['finance', 'petty-cash', locationId], queryFn: ({ signal }) => api<AccountLedger>(`/api/petty-cash/${locationId}/statement`, { signal }), enabled: Boolean(locationId) })

export const useAssets = () => useQuery({ queryKey: ['finance', 'assets'], queryFn: ({ signal }) => api<AssetListItem[]>('/api/assets', { signal }) })
export const useAsset = (id: string) => useQuery({ queryKey: ['finance', 'asset', id], queryFn: ({ signal }) => api<AssetDetail>(`/api/assets/${id}`, { signal }) })

export const useOpeningStatus = () => useQuery({ queryKey: ['finance', 'opening'], queryFn: ({ signal }) => api<OpeningStatus>('/api/finance/opening-balances', { signal }) })

function useFinanceMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => client.invalidateQueries({ queryKey: financeKeys.all }) })
}

export const useCreateAccount = () =>
  useFinanceMutation((input: { code: string; name: string; type: AccountType; isBankAccount: boolean }) => api<Account>('/api/finance/accounts', { method: 'POST', body: input }))

export const useRenameAccount = (id: string) =>
  useFinanceMutation((name: string) => api<void>(`/api/finance/accounts/${id}`, { method: 'PUT', body: { name } }))

export const useAccountStatus = (id: string) =>
  useFinanceMutation((action: 'activate' | 'deactivate') => api<void>(`/api/finance/accounts/${id}/${action}`, { method: 'POST' }))

export const usePostJournal = () =>
  useFinanceMutation(({ input, idempotencyKey }: { input: { entryDate: string; narration: string; lines: { accountCode: string; debit: number; credit: number; locationId?: string; narration?: string }[] }; idempotencyKey: string }) =>
    api<JournalDetail>('/api/finance/journals', { method: 'POST', body: input, idempotencyKey }))

export const useReverseJournal = (id: string) =>
  useFinanceMutation((reason: string) => api<JournalDetail>(`/api/finance/journals/${id}/reverse`, { method: 'POST', body: { reason } }))

export const usePeriodAction = () =>
  useFinanceMutation(({ year, month, action, reason }: { year: number; month: number; action: 'lock' | 'reopen'; reason?: string }) =>
    api<void>(`/api/finance/periods/${year}/${month}/${action}`, { method: 'POST', body: action === 'reopen' ? { reason } : undefined }))

export const useCloseYear = () => useFinanceMutation((year: number) => api<YearEnd>(`/api/finance/year-ends/${year}`, { method: 'POST' }))

export const useCreateExpense = () =>
  useFinanceMutation(({ input, idempotencyKey }: {
    input: { locationId: string; expenseDate?: string; categoryId: string; payee: string; description: string; netAmount: number; vatAmount: number; taxInvoiceNumber?: string; paidFrom: ExpensePaidFrom; paidFromAccountCode?: string; reference?: string }
    idempotencyKey: string
  }) => api<ExpenseDetail>('/api/expenses', { method: 'POST', body: input, idempotencyKey }))

export const useExpenseAction = (id: string) =>
  useFinanceMutation(({ action, reason }: { action: 'approve' | 'reject'; reason?: string }) =>
    api<ExpenseDetail>(`/api/expenses/${id}/${action}`, { method: 'POST', body: action === 'reject' ? { reason } : undefined }))

export const useAttachReceipt = (id: string) =>
  useFinanceMutation((file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api<ExpenseDetail>(`/api/expenses/${id}/attachments`, { method: 'POST', body: form })
  })

export const useSetFloat = () =>
  useFinanceMutation(({ locationId, floatAmount }: { locationId: string; floatAmount: number }) =>
    api<void>(`/api/petty-cash/${locationId}`, { method: 'PUT', body: { floatAmount } }))

export const useTopUp = () =>
  useFinanceMutation(({ locationId, amount, fromAccountCode, reference, idempotencyKey }: { locationId: string; amount: number; fromAccountCode: string; reference?: string; idempotencyKey: string }) =>
    api<void>(`/api/petty-cash/${locationId}/top-up`, { method: 'POST', body: { amount, fromAccountCode, reference }, idempotencyKey }))

export const useCountCash = () =>
  useFinanceMutation(({ locationId, counted, note }: { locationId: string; counted: number; note?: string }) =>
    api<{ difference: number; position: PettyCashRow }>(`/api/petty-cash/${locationId}/count`, { method: 'POST', body: { counted, note } }))

export const useAcquireAsset = () =>
  useFinanceMutation((input: { name: string; category: AssetCategory; locationId: string; purchaseDate: string; cost: number; residualValue: number; usefulLifeMonths: number; serialNumber?: string; supplier?: string; notes?: string; paidFromAccountCode: string }) =>
    api<AssetDetail>('/api/assets', { method: 'POST', body: input }))

export const useAssetAction = (id: string) =>
  useFinanceMutation(({ action, body }: { action: 'maintenance' | 'dispose'; body: unknown }) =>
    api<unknown>(`/api/assets/${id}/${action}`, { method: 'POST', body }))

export const useRunDepreciation = () =>
  useFinanceMutation((input: { year: number; month: number }) =>
    api<{ year: number; month: number; assetsCharged: number; total: number; journalCode?: string }>('/api/assets/depreciation', { method: 'POST', body: input }))

export function usePreviewOpening() {
  return useMutation({ mutationFn: (input: OpeningBalances) => api<OpeningPreview>('/api/finance/opening-balances/preview', { method: 'POST', body: input }) })
}

export const usePostOpening = () =>
  useFinanceMutation(({ input, idempotencyKey }: { input: OpeningBalances; idempotencyKey: string }) =>
    api<OpeningStatus>('/api/finance/opening-balances', { method: 'POST', body: input, idempotencyKey }))
